import React, { useCallback, useEffect, useState } from "react";
import BookingShell, { api } from "./BookingShell";
import { logActivity } from "../../utils/activity";
import { priceTiers, sizesOf, walkInPrice } from "./pricing";
import { formatKr, formatTime } from "../../utils/format";

const EMPTY_FORM = { first_name: "", last_name: "", phone: "", email: "", remark: "" };

// WalkInPage creates a booking in the room's calendar for a group standing in the lobby.
export default function WalkInPage() {
    return (
        <BookingShell title="Walk-in" activity="open_walkin">
            {room => <WalkIn key={room.calendar_id} room={room} />}
        </BookingShell>
    );
}

function WalkIn({ room }) {
    const [slots, setSlots] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [slotId, setSlotId] = useState("");
    // tier is the price step chosen in the menu — a size, or "more" for a group above the largest
    // size sold. players is the head count that goes into the booking. They differ when a step
    // covers several group sizes ("2-4") or when the group is over the top step.
    const [tier, setTier] = useState("");
    const [players, setPlayers] = useState("");
    const [form, setForm] = useState(EMPTY_FORM);
    const [submitting, setSubmitting] = useState(false);
    const [message, setMessage] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        setError("");
        try {
            const r = await api(`gm-booking/slots?calendar_id=${encodeURIComponent(room.calendar_id)}`);
            if (!r.ok) throw new Error(String(r.status));
            const data = await r.json();
            setSlots(data.slots || []);
        } catch {
            setError("Kunne ikke hente tiderne fra kalenderen.");
        } finally {
            setLoading(false);
        }
    }, [room.calendar_id]);

    useEffect(() => { load(); }, [load]);

    const slot = slots.find(s => s.id === slotId) || null;
    const sizes = sizesOf(slot?.price_per_person);
    const maxSize = sizes.length ? sizes[sizes.length - 1] : 0;
    // The cheapest step reaches down to the room minimum (two), so a band sold to 4 and up reads
    // "2-4 spillere" — see priceTiers.
    const tiers = slot ? priceTiers(slot.price_per_person) : [];
    // The head count is always asked for, not only when the step covers a range: the booking should
    // say how many actually turned up, while the price stays the step's.
    const countable = tier !== "" && sizes.length > 0;
    const price = slot && players ? walkInPrice(slot.price_per_person, Number(players)) : null;
    const total = price && !price.error ? price.amount : null;

    function pick(s) {
        setSlotId(s.id);
        setMessage(null);
        // The steps differ per time band, so a step chosen for another slot means nothing here.
        setTier("");
        setPlayers("");
    }

    // chooseSize picks a price step; players follows it at the smallest group the step covers, and
    // the GM raises it to the number that actually turned up.
    function chooseSize(value) {
        setTier(value);
        if (value === "more") {
            setPlayers(String(maxSize + 1));
            return;
        }
        const step = tiers.find(t => String(t.size) === value);
        setPlayers(step ? String(step.from) : value);
    }

    const set = field => e => setForm(f => ({ ...f, [field]: e.target.value }));

    const canSubmit = slot && slot.status === "open" && Number(players) > 0 && form.first_name.trim() && !submitting;

    async function submit(e) {
        e.preventDefault();
        if (!canSubmit) return;
        setSubmitting(true);
        setMessage(null);
        try {
            const r = await api("gm-booking/walkin", {
                method: "POST",
                body: JSON.stringify({ calendar_id: room.calendar_id, event_id: slot.id, players: Number(players), ...form }),
            });
            if (r.status === 201) {
                logActivity("gm_walkin", `${room.calendar_id} ${slot.start_time} ${players}`);
                setMessage({
                    ok: true,
                    text: `Walk-in oprettet i ${room.name} kl. ${formatTime(slot.start_time)} for ${players} spillere` +
                        (total != null ? ` — ${formatKr(total)}` : "") + ".",
                });
                setForm(EMPTY_FORM);
                setPlayers("");
                setSlotId("");
                load();
            } else if (r.status === 409) {
                setMessage({ ok: false, text: "Tiden er lige blevet booket af en anden. Vælg en anden tid." });
                load();
            } else if (r.status === 404) {
                setMessage({ ok: false, text: "Tiden findes ikke længere, eller den startede for mere end 30 minutter siden." });
                load();
            } else {
                setMessage({ ok: false, text: "Bookingen kunne ikke oprettes. Prøv igen." });
            }
        } catch {
            setMessage({ ok: false, text: "Bookingen kunne ikke oprettes. Prøv igen." });
        } finally {
            setSubmitting(false);
        }
    }

    const now = Date.now();

    return (
        <form className="bk-card" onSubmit={submit}>
            <div className="bk-card-head">
                <h2>Walk-in</h2>
                <button type="button" className="bk-link" onClick={load} disabled={loading}>
                    {loading ? "Henter…" : "Opdater"}
                </button>
            </div>
            <p className="bk-dim">Tider fra 30 minutter siden og resten af dagen.</p>

            {error && <div className="bk-error">{error}</div>}
            {!loading && !error && slots.length === 0 && <div className="bk-empty">Ingen åbne tider lige nu.</div>}

            <div className="bk-slots">
                {slots.map(s => {
                    const startedMin = Math.floor((now - new Date(s.start_time).getTime()) / 60000);
                    const bookable = s.status === "open";
                    return (
                        <button
                            type="button"
                            key={s.id}
                            className={`bk-slot ${s.id === slotId ? "active" : ""}`}
                            disabled={!bookable}
                            onClick={() => pick(s)}
                        >
                            <span className="bk-slot-time">{formatTime(s.start_time)}–{formatTime(s.end_time)}</span>
                            <span className="bk-slot-sub">
                                {!bookable
                                    ? (s.status === "occupied" ? "Optaget" : "Ikke tilgængelig")
                                    : startedMin > 0 ? `Startet for ${startedMin} min. siden` : "Ledig"}
                            </span>
                        </button>
                    );
                })}
            </div>

            {slot && (
                <>
                    <label className="bk-field">
                        Antal spillere
                        {sizes.length > 0 ? (
                            <select value={tier} onChange={e => chooseSize(e.target.value)}>
                                <option value="">Vælg…</option>
                                {tiers.map(t => (
                                    <option key={t.size} value={t.size}>
                                        {t.from === t.size ? t.size : `${t.from}-${t.size}`} spillere — {formatKr(t.total)} ({formatKr(t.perPerson)} pr. person)
                                    </option>
                                ))}
                                <option value="more">Flere end {maxSize} spillere…</option>
                            </select>
                        ) : (
                            <input type="number" min="1" max="50" value={players} onChange={e => setPlayers(e.target.value)} />
                        )}
                    </label>
                    {countable && (
                        <label className="bk-field">
                            Hvor mange kommer der?
                            <input type="number" min="1" max="50" value={players} onChange={e => setPlayers(e.target.value)} />
                        </label>
                    )}
                    {price?.belowMin && (
                        <div className="bk-note">
                            {players} spillere betaler prisen for {price.tier}. Færre end {price.tier} sælges ikke på dette tidspunkt.
                        </div>
                    )}
                    {price?.overMax && (
                        <div className="bk-warn">
                            {players} spillere er flere, end rummet normalt sælges til — højst {price.max}.
                            Alle betaler prisen for {price.max} spillere, {formatKr(price.perPerson)} pr. person.
                        </div>
                    )}
                    {sizes.length === 0 && (
                        <div className="bk-note">Ingen pris i prisreglerne for dette tidspunkt — brug prislisten.</div>
                    )}

                    <div className="bk-grid">
                        <label className="bk-field">Fornavn *<input value={form.first_name} onChange={set("first_name")} required /></label>
                        <label className="bk-field">Efternavn<input value={form.last_name} onChange={set("last_name")} /></label>
                        <label className="bk-field">Telefon<input type="tel" value={form.phone} onChange={set("phone")} /></label>
                        <label className="bk-field">
                            E-mail
                            <input type="email" value={form.email} onChange={set("email")} placeholder="Valgfri" />
                        </label>
                    </div>
                    {form.email.trim() && <div className="bk-note">Kunden får en kalenderinvitation på e-mail.</div>}
                    <label className="bk-field">
                        Bemærkning — kunden kan se den
                        <input value={form.remark} onChange={set("remark")} placeholder="Fx betalt på kort" />
                    </label>
                    <div className="bk-note">
                        Bemærkningen står i bookingen og er med i kalenderinvitationen, hvis kunden får en.
                    </div>

                    {total != null && (
                        <div className="bk-price">
                            <span>At betale</span>
                            <strong>{formatKr(total)}</strong>
                        </div>
                    )}

                    <button type="submit" className="topbar-btn accent bk-submit" disabled={!canSubmit}>
                        {submitting ? "Opretter…" : `Opret walk-in kl. ${formatTime(slot.start_time)}`}
                    </button>
                </>
            )}

            {message && <div className={message.ok ? "bk-success" : "bk-error"}>{message.text}</div>}
        </form>
    );
}
